import { useEditor } from '../state/store';

/**
 * Shown when the editor boots and detects that the default theme
 * advanced since the tenant's last sync. Two variants:
 *
 *   - success: tema atualizado, mostra quantos arquivos + settings novos
 *   - error:   sync falhou (rede, engine, S3). Aparece pra que o lojista
 *              saiba que a versão em prod pode estar atrás do editor e
 *              tenha um motivo pra pedir suporte. Antes o erro era
 *              silenciado em `console.warn` — Bruno descobriu isso quando
 *              a loja da cremecacau ficou meses servindo a versão velha.
 */
export function ThemeSyncBanner() {
  const report = useEditor((s) => s.themeSync);
  const errorMsg = useEditor((s) => s.themeSyncError);
  const dismiss = useEditor((s) => s.dismissThemeSync);
  const dismissError = useEditor((s) => s.dismissThemeSyncError);

  if (errorMsg) {
    return (
      <div
        role="alert"
        className="flex items-start gap-3 border-b border-critical/30 bg-critical-subdued px-4 py-2.5"
      >
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-critical/15 text-critical">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-text">
            Falha ao sincronizar a versão mais recente do tema
          </div>
          <div className="text-[12px] text-text-subdued">
            A sua loja em prod pode continuar servindo a versão anterior.
            Recarregue a página; se o erro persistir, avise o suporte.
          </div>
          <div className="mt-1 break-words rounded bg-critical/10 px-2 py-1 font-mono text-[11px] text-critical">
            {errorMsg}
          </div>
        </div>
        <button
          onClick={() => dismissError()}
          className="lc-btn-ghost flex h-7 w-7 shrink-0 items-center justify-center"
          aria-label="Dispensar"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  if (!report || !report.upgraded) return null;

  const filesTotal = report.files.overwritten + report.files.created;
  const addedCount = report.settings.added.length;

  return (
    <div
      role="status"
      className="flex items-center gap-3 border-b border-success/30 bg-success-subdued px-4 py-2.5"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-6.2-8.5" />
          <path d="M21 4v5h-5" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-text">
          Tema atualizado para a versão mais recente
        </div>
        <div className="text-[12px] text-text-subdued">
          {filesTotal} arquivo{filesTotal === 1 ? '' : 's'} sincronizado
          {filesTotal === 1 ? '' : 's'}
          {addedCount > 0 && (
            <>
              {' · '}
              {addedCount} nova{addedCount === 1 ? '' : 's'} configuração
              {addedCount === 1 ? '' : 's'} com valor padrão
            </>
          )}
          . Suas personalizações foram preservadas.
        </div>
      </div>
      <button
        onClick={() => dismiss()}
        className="lc-btn-ghost flex h-7 w-7 items-center justify-center"
        aria-label="Dispensar"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
